import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { PhoenixLogo } from '../components/PhoenixLogo';
import { PurchaseReminderModal } from '../components/PurchaseReminderModal';
import { C } from '../utils/constants';

export const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState(() => localStorage.getItem('ry_remembered_email') || '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem('ry_remembered_email'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (rememberMe) {
        localStorage.setItem('ry_remembered_email', email);
      } else {
        localStorage.removeItem('ry_remembered_email');
      }
      const user = await login(email, password);
      if (!user.onboardingComplete) {
        navigate('/onboarding', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Invalid email or password. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PurchaseReminderModal />
      {/* Dark hero section */}
      <div style={{
        background: `linear-gradient(180deg, #1a0800 0%, #3d1f02 60%, ${C.cream} 100%)`,
        padding: '60px 24px 80px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 16,
      }}>
        <PhoenixLogo size={220} />

        <h1 style={{
          fontFamily: "'Forum', serif",
          color: '#fff',
          fontSize: 'clamp(1.4rem, 5vw, 2.2rem)',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          marginTop: 8,
        }}>
          Recondition Yourself
        </h1>

        <p style={{
          fontFamily: "'Forum', serif",
          color: C.gold,
          fontSize: '1.1rem',
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          fontWeight: 700,
        }}>
          Client Portal
        </p>
      </div>

      {/* Login card */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '0 16px 48px',
        marginTop: -40,
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: '36px 32px',
          width: '100%',
          maxWidth: 420,
          boxShadow: '0 8px 40px rgba(61,31,2,0.15)',
        }}>
          <h2 style={{
            fontFamily: "'Forum', serif",
            color: C.brown,
            fontSize: '1.6rem',
            letterSpacing: '0.03em',
            marginBottom: 6,
          }}>
            Welcome
          </h2>
          <p style={{
            color: C.terracotta,
            fontSize: '0.88rem',
            marginBottom: 28,
          }}>
            Sign in to continue your journey
          </p>

          <form onSubmit={handleSubmit}>
            {/* Error message */}
            {error && (
              <div style={{
                background: 'rgba(173,121,91,0.12)',
                border: `1px solid ${C.terracotta}`,
                borderRadius: 8,
                padding: '10px 14px',
                marginBottom: 20,
                color: C.terracotta,
                fontSize: '0.85rem',
                lineHeight: 1.4,
              }}>
                {error}
              </div>
            )}

            {/* Email */}
            <div style={{ marginBottom: 18 }}>
              <label style={{
                display: 'block',
                fontSize: '0.8rem',
                fontWeight: 600,
                color: C.brown,
                marginBottom: 6,
                letterSpacing: '0.04em',
              }}>
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: 8 }}>
              <label style={{
                display: 'block',
                fontSize: '0.8rem',
                fontWeight: 600,
                color: C.brown,
                marginBottom: 6,
                letterSpacing: '0.04em',
              }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
            </div>

            {/* Remember me + Forgot password */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem', color: C.brown }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: C.brown, cursor: 'pointer' }}
                />
                Remember me
              </label>
              <Link
                to="/forgot-password"
                style={{
                  fontSize: '0.78rem',
                  color: C.terracotta,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
              style={{ width: '100%', fontSize: '1rem' }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>

        {/* Footer text */}
        <div style={{
          marginTop: 28,
          textAlign: 'center',
          opacity: 0.7,
        }}>
          <p style={{
            fontSize: '1rem',
            fontWeight: 800,
            color: C.brown,
            letterSpacing: '0.06em',
            marginBottom: 2,
          }}>
            BM
          </p>
          <p style={{
            fontSize: '0.72rem',
            color: C.terracotta,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          }}>
            Transform Your Mind · Transcend Your Life
          </p>
        </div>
      </div>
    </div>
  );
};
